/**
 * The default toolbar items.
 *
 * Every one is a thin binding of a core command to a label and an icon. No
 * editing logic lives here -- if a button needs to know something about the
 * document, that knowledge belongs in `@openleaf-editor/core` where a keyboard
 * shortcut and a test can reach it too.
 */

import {
  activeFontFamily,
  activeFontSize,
  activeLineHeight,
  activeLink,
  activeTextAlign,
  FONT_FAMILIES,
  FONT_SIZE_PRESETS,
  indent,
  insertHorizontalRule,
  insertImage,
  isMarkActive,
  isNodeActive,
  LINE_HEIGHT_PRESETS,
  outdent,
  redo,
  safeFontFamily,
  selectedImage,
  setFontFamily,
  setFontSize,
  setLineHeight,
  setLink,
  setTextAlign,
  toggleBlockquote,
  toggleBold,
  toggleBulletList,
  toggleCodeBlock,
  toggleInlineCode,
  toggleItalic,
  toggleOrderedList,
  toggleStrike,
  toggleTextAlign,
  toggleUnderline,
  undo,
  unsetLink,
  updateImage,
  type Align,
} from '@openleaf-editor/core'
import type { Command, EditorState } from 'prosemirror-state'
import { TextSelection } from 'prosemirror-state'
import type { EditorView } from 'prosemirror-view'
import { promptForImage, promptForLink } from './dialog.js'
import { promptHelp } from './help.js'
import type { IconName } from './icons.js'
import { getToolbarItem, registerToolbarItem } from './registry.js'
import { imageUploaderFor, runUploader } from './upload.js'
import { blockTypeControl } from './block-type.js'

/** Event the host listens for to switch between rich and source views. */
export const SOURCE_TOGGLE_EVENT = 'openleaf:toggle-source'
export const FULLSCREEN_TOGGLE_EVENT = 'openleaf:toggle-fullscreen'
export const VISUAL_AIDS_TOGGLE_EVENT = 'openleaf:toggle-visual-aids'

/** Whether anything in the selection can be aligned, and the alignment in force. */
type AlignFacts = { readonly any: boolean; readonly align: Align | null }

const alignCache = new WeakMap<EditorState, AlignFacts>()

/**
 * One selection walk per state, shared by every alignment button on every bar.
 *
 * Each of the four buttons used to answer `isEnabled` by dry-running its own
 * command, and `toggleTextAlign` walks the selection twice on its own -- twelve
 * identical walks of every selected block, per transaction, per toolbar. With
 * two bars and two floating bars over a Select-All on a hundred-page document
 * that was ~48,000 wasted node visits inside `dispatchTransaction`.
 *
 * Keyed on the state, so the second, third and fourth toolbar reading the same
 * transaction pay nothing. Answers come from core (`setTextAlign` /
 * `activeTextAlign`) so a selected image lights the same button the command
 * will write, rather than a second walk that only saw textblocks.
 */
function alignFacts(state: EditorState): AlignFacts {
  const hit = alignCache.get(state)
  if (hit) return hit
  const facts: AlignFacts = {
    any: setTextAlign('left')(state),
    align: activeTextAlign(state),
  }
  alignCache.set(state, facts)
  return facts
}

/** Register the built-in items. Idempotent. */
/**
 * The span of the link the caret sits in, or null when it is not in one.
 *
 * `setLink` and `unsetLink` both refuse an empty selection, which is right --
 * there is nothing to mark. But an author who right-clicks a link, or puts the
 * caret in one and presses the toolbar button, means "this link", and the range
 * of a mark is something only the document can answer.
 */
function linkRange(state: EditorState): { from: number; to: number } | null {
  const type = state.schema.marks['link']
  if (!type || !state.selection.empty) return null
  const $pos = state.selection.$from
  const mark = type.isInSet($pos.marks())
  if (!mark) return null
  const parent = $pos.parent
  let start = $pos.index()
  let end = $pos.indexAfter()
  while (start > 0 && mark.isInSet(parent.child(start - 1).marks)) start -= 1
  while (end < parent.childCount && mark.isInSet(parent.child(end).marks)) end += 1
  let from = $pos.start()
  for (let i = 0; i < start; i += 1) from += parent.child(i).nodeSize
  let to = from
  for (let i = start; i < end; i += 1) to += parent.child(i).nodeSize
  return { from, to }
}

/** Widen a caret inside a link to cover the whole link, so a command can act. */
function selectLink(view: EditorView): void {
  const range = linkRange(view.state)
  if (!range) return
  view.dispatch(
    view.state.tr.setSelection(TextSelection.create(view.state.doc, range.from, range.to)),
  )
}

export function registerDefaultItems(): void {
  if (getToolbarItem('undo') && getToolbarItem('blockType') && getToolbarItem('source')) return

  registerToolbarItem({
    id: 'blockType',
    // `custom`, not `select`: it builds its own DOM because it carries formats
    // the host injects at mount time. `type: 'select'` is the declarative
    // contract for a fixed preset list, which this is not.
    type: 'custom',
    kind: 'action',
    label: 'Paragraph style',
    render: ({ view, host, formats }) => blockTypeControl(view, host, formats ?? []),
  })

  // `scope: 'document'` on both: history is the one pair here that ignores the
  // selection entirely and acts on the last event anywhere in the document. It
  // changes nothing about the button; it is what tells a caller running a
  // command against a staged range that this is not one of those.
  registerToolbarItem({
    id: 'undo',
    type: 'button',
    kind: 'action',
    label: 'Undo',
    icon: 'undo',
    command: undo,
    scope: 'document',
    shortcut: 'Undo',
  })

  registerToolbarItem({
    id: 'redo',
    type: 'button',
    kind: 'action',
    label: 'Redo',
    icon: 'redo',
    command: redo,
    scope: 'document',
    shortcut: 'Redo',
  })

  /* ---- character marks ---- */

  interface MarkItem {
    id: string
    label: string
    icon: IconName
    /** Schema mark name the active predicate asks about. */
    mark: string
    command: Command
  }

  const markItems: MarkItem[] = [
    { id: 'bold', label: 'Bold', icon: 'bold', mark: 'strong', command: toggleBold },
    { id: 'italic', label: 'Italic', icon: 'italic', mark: 'em', command: toggleItalic },
    {
      id: 'underline',
      label: 'Underline',
      icon: 'underline',
      mark: 'underline',
      command: toggleUnderline,
    },
    {
      id: 'strikethrough',
      label: 'Strikethrough',
      icon: 'strikethrough',
      mark: 'strike',
      command: toggleStrike,
    },
    { id: 'code', label: 'Inline code', icon: 'code', mark: 'code', command: toggleInlineCode },
  ]

  for (const item of markItems) {
    registerToolbarItem({
      id: item.id,
      type: 'button',
      kind: 'toggle',
      label: item.label,
      icon: item.icon,
      command: item.command,
      isActive: (state) => isMarkActive(state, item.mark),
      shortcut: item.label,
    })
  }

  /* ---- typography ----
     Preset selects over core's font / size / line-height commands. The schema
     already models these so inherited HTML stays editable; these controls are
     the chrome that was missing from the default bar. */

  registerToolbarItem({
    id: 'fontFamily',
    type: 'select',
    label: 'Font family',
    selectMod: 'wide',
    options: [
      { value: '', label: 'Default' },
      // Option values are the canonical spelling `safeFontFamily` stores:
      // a CSS identifier unquoted, everything else in double quotes. A stored
      // single-quoted family is rewritten to that form on parse, so getValue
      // matches the option rather than snapping the dropdown to Default.
      ...FONT_FAMILIES.map((family) => ({
        value: safeFontFamily(family) ?? family,
        label: family,
      })),
    ],
    getValue: (state) => activeFontFamily(state) ?? '',
    applyValue: (value) => setFontFamily(value === '' ? null : value),
  })

  registerToolbarItem({
    id: 'fontSize',
    type: 'select',
    label: 'Font size',
    options: [
      { value: '', label: 'Default' },
      ...FONT_SIZE_PRESETS.map((px) => ({ value: `${px}px`, label: String(px) })),
    ],
    getValue: (state) => activeFontSize(state) ?? '',
    applyValue: (value) => setFontSize(value === '' ? null : value),
  })

  registerToolbarItem({
    id: 'lineHeight',
    type: 'select',
    label: 'Line height',
    options: [
      { value: '', label: 'Default' },
      ...LINE_HEIGHT_PRESETS.map((value) => ({ value, label: value })),
    ],
    getValue: (state) => activeLineHeight(state) ?? '',
    applyValue: (value) => setLineHeight(value === '' ? null : value),
  })

  /* ---- block structure ----
     These get toggle semantics too. A blockquote or a list is a state the
     cursor is either in or not, exactly like bold, and leaving them as plain
     actions means a screen reader user cannot tell whether they are inside a
     list without moving the caret and inferring it. */

  registerToolbarItem({
    id: 'bulletList',
    type: 'button',
    kind: 'toggle',
    label: 'Bulleted list',
    icon: 'bulletList',
    command: toggleBulletList,
    isActive: (state) => isNodeActive(state, 'bullet_list'),
    shortcut: 'Bulleted list',
  })

  registerToolbarItem({
    id: 'orderedList',
    type: 'button',
    kind: 'toggle',
    label: 'Numbered list',
    icon: 'orderedList',
    command: toggleOrderedList,
    isActive: (state) => isNodeActive(state, 'ordered_list'),
    shortcut: 'Numbered list',
  })

  registerToolbarItem({
    id: 'blockquote',
    type: 'button',
    kind: 'toggle',
    label: 'Blockquote',
    icon: 'blockquote',
    command: toggleBlockquote,
    isActive: (state) => isNodeActive(state, 'blockquote'),
    shortcut: 'Blockquote',
  })

  registerToolbarItem({
    id: 'codeBlock',
    type: 'button',
    kind: 'toggle',
    label: 'Code block',
    icon: 'codeBlock',
    command: toggleCodeBlock,
    isActive: (state) => isNodeActive(state, 'code_block'),
    shortcut: 'Code block',
  })

  /* ---- alignment ----
     Toggles, not actions, and the pressed state is the EXPLICIT alignment only.
     A paragraph nobody has aligned follows the document's reading direction, so
     lighting up the left button for it would be wrong in Arabic and Hebrew --
     and `aria-pressed="true"` on a paragraph that is not left-aligned is a lie
     a screen reader repeats. See activeTextAlign in core. */

  const alignItems: Array<{ id: string; label: string; icon: IconName; align: Align }> = [
    { id: 'alignLeft', label: 'Align left', icon: 'alignLeft', align: 'left' },
    { id: 'alignCenter', label: 'Align centre', icon: 'alignCenter', align: 'center' },
    { id: 'alignRight', label: 'Align right', icon: 'alignRight', align: 'right' },
    { id: 'alignJustify', label: 'Justify', icon: 'alignJustify', align: 'justify' },
  ]

  for (const item of alignItems) {
    registerToolbarItem({
      id: item.id,
      type: 'button',
      kind: 'toggle',
      label: item.label,
      icon: item.icon,
      command: toggleTextAlign(item.align),
      // Both predicates come off one cached walk. Without the explicit
      // isEnabled the toolbar dry-runs `command`, and toggleTextAlign walks the
      // selection twice on its own -- see alignFacts.
      isEnabled: (state) => alignFacts(state).any,
      isActive: (state) => alignFacts(state).align === item.align,
      shortcut: item.label,
    })
  }

  registerToolbarItem({
    id: 'indent',
    type: 'button',
    kind: 'action',
    label: 'Indent',
    icon: 'indent',
    command: indent,
    shortcut: 'Indent',
  })

  registerToolbarItem({
    id: 'outdent',
    type: 'button',
    kind: 'action',
    label: 'Outdent',
    icon: 'outdent',
    command: outdent,
    shortcut: 'Outdent',
  })

  /* ---- insertions ---- */

  registerToolbarItem({
    id: 'link',
    type: 'button',
    kind: 'toggle',
    label: 'Link',
    icon: 'link',
    // A selection to attach the link to, or a caret already inside one. Those
    // two used to disagree: `isActive` said "in a link" while `isEnabled` said
    // "there is a selection", so right-clicking a link -- which places a
    // COLLAPSED caret -- rendered a control that was aria-pressed and
    // aria-disabled at once and refused the click. The context menu built for
    // exactly that gesture could therefore never edit a link, and the "Edit
    // link" dialog title was unreachable.
    isEnabled: (state) => !state.selection.empty || linkRange(state) !== null,
    isActive: (state) => activeLink(state) !== null,
    run: ({ view, host }) => {
      selectLink(view)
      const existing = activeLink(view.state)
      // All five modelled attrs: dropping `rel`/`id` here is how Save used to
      // wipe them even when the author changed nothing (#108).
      void promptForLink(host.ownerDocument, {
        href: (existing?.['href'] as string | undefined) ?? '',
        title: (existing?.['title'] as string | undefined) ?? null,
        target: (existing?.['target'] as string | undefined) ?? null,
        rel: (existing?.['rel'] as string | undefined) ?? null,
        id: (existing?.['id'] as string | undefined) ?? null,
      }, host).then((result) => {
        if (!result) {
          view.focus()
          return
        }
        setLink(result)(view.state, view.dispatch, view)
        view.focus()
      })
    },
  })

  registerToolbarItem({
    id: 'unlink',
    type: 'button',
    kind: 'action',
    label: 'Remove link',
    icon: 'unlink',
    isEnabled: (state) => unsetLink(state) || linkRange(state) !== null,
    run: ({ view }) => {
      selectLink(view)
      unsetLink(view.state, view.dispatch, view)
      view.focus()
    },
  })

  registerToolbarItem({
    id: 'image',
    type: 'button',
    kind: 'action',
    label: 'Insert image',
    icon: 'image',
    // Insert and edit are the same control, as they are for media: a separate
    // "image properties" item is one the author has to discover, and without
    // it there is no way to revise alt text after insert.
    labelFor: (state) => (selectedImage(state) ? 'Edit image' : 'Insert image'),
    run: ({ view, host }) => {
      // One item, two dialogs, decided by whether this page can upload. A file
      // picker that appears and then fails because no uploader was registered is
      // worse than no picker: the author has already chosen the file.
      const uploader = imageUploaderFor(host)
      const current = selectedImage(view.state)
      const options = {
        host,
        ...(uploader ? { upload: (file: File) => runUploader(uploader, file, host) } : {}),
        ...(current
          ? {
              existing: {
                src: current.src,
                alt: current.alt,
                title: current.title,
                className: current.className,
                align: current.align,
                caption: current.caption,
                width: current.width,
                height: current.height,
              },
            }
          : {}),
      }

      void promptForImage(host.ownerDocument, options).then((result) => {
        if (view.isDestroyed) return
        if (!result) {
          view.focus()
          return
        }
        const attrs = {
          src: result.src,
          alt: result.alt,
          title: result.title,
          width: result.width,
          height: result.height,
          align: result.align,
          className: result.className,
          caption: result.caption,
        }
        if (current) {
          updateImage(attrs)(view.state, view.dispatch, view)
        } else {
          insertImage({
            src: result.src,
            alt: result.alt,
            title: result.title,
            width: result.width,
            height: result.height,
            align: result.align,
            className: result.className,
            ...(result.caption ? { caption: result.caption } : {}),
          })(view.state, view.dispatch, view)
        }
        view.focus()
      })
    },
  })

  registerToolbarItem({
    id: 'horizontalRule',
    type: 'button',
    kind: 'action',
    label: 'Horizontal rule',
    icon: 'horizontalRule',
    command: insertHorizontalRule,
    shortcut: 'Horizontal rule',
  })

  /* ---- mode ---- */

  registerToolbarItem({
    id: 'source',
    type: 'button',
    kind: 'toggle',
    label: 'HTML source',
    icon: 'source',
    // Source view is host state, not document state, so no predicate can derive
    // it. The host pushes the value with setItemState -- which is exactly why
    // that escape hatch exists.
    isActive: () => false,
    run: ({ host }) => {
      host.dispatchEvent(new CustomEvent(SOURCE_TOGGLE_EVENT, { bubbles: true, composed: true }))
    },
  })

  registerToolbarItem({
    id: 'fullscreen',
    type: 'button',
    kind: 'toggle',
    label: 'Fullscreen',
    icon: 'fullscreen',
    isActive: () => false,
    run: ({ host }) => {
      host.dispatchEvent(new CustomEvent(FULLSCREEN_TOGGLE_EVENT, { bubbles: true, composed: true }))
    },
  })

  registerToolbarItem({
    id: 'visualAids',
    type: 'button',
    kind: 'toggle',
    label: 'Visual aids',
    icon: 'visualAids',
    isActive: () => false,
    run: ({ host }) => {
      host.dispatchEvent(new CustomEvent(VISUAL_AIDS_TOGGLE_EVENT, { bubbles: true, composed: true }))
    },
  })

  registerToolbarItem({
    id: 'help',
    type: 'button',
    kind: 'action',
    label: 'Help',
    icon: 'help',
    run: ({ host }) => {
      promptHelp(host.ownerDocument, host)
    },
  })
}
